import { ChevronRight, ShoppingBag } from 'lucide-react';

export interface ProductShopBannerProps {
    /** Marketplace, in stock, selected variant purchasable. */
    isBuyable: boolean;
    /** A sample can be ordered (independent of full buyability). */
    isSampleBuyable: boolean;
    /** Opens the shop sheet (the variant sheet, footer led by the ecom CTA). */
    onOpen: () => void;
}

/**
 * "Available to shop on ArchiPro" — the opt-in ecom entry point on the mobile
 * PDP. Enquiry is the page's primary action (QDD); for the minority of products
 * that can actually be bought or sampled on ArchiPro, this keeps that path one
 * tap away without competing with Enquire for the primary slot. Tapping opens
 * the existing variant sheet with its ecom footer (quantity + Add to Cart /
 * Order Sample).
 *
 * Shares the revamp's editorial row language — the circular badge, ink/grey
 * type and hairline divider used by the supplier trust row beside it — rather
 * than a boxed callout. Self-hides when there is nothing to transact.
 */
export const ProductShopBanner = ({
    isBuyable,
    isSampleBuyable,
    onOpen,
}: ProductShopBannerProps) => {
    if (!isBuyable && !isSampleBuyable) return null;

    // Buyable leads with the full purchase; sample-only offers the sample.
    const subtitle = isBuyable
        ? 'Add to cart and check out securely'
        : 'Order a sample, delivered to your door';

    return (
        <button
            type="button"
            onClick={onOpen}
            className="group flex w-full items-center gap-3 py-4 text-left"
        >
            <span className="flex size-12 shrink-0 items-center justify-center rounded-full border border-black/10 text-[#1f1f21]">
                <ShoppingBag className="size-[1.15rem]" strokeWidth={1.5} />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="font-sans text-base leading-[1.2] font-medium tracking-[-0.02em] text-[#1f1f21]">
                    Available to shop on ArchiPro
                </span>
                <span className="font-sans text-[0.8125rem] leading-[1.2] text-[#767676]">
                    {subtitle}
                </span>
            </span>
            <ChevronRight className="size-5 shrink-0 text-[#767676] transition-transform duration-200 ease-out group-hover:translate-x-0.5" />
        </button>
    );
};

/**
 * The desktop counterpart of the shop banner: a single quiet line under the
 * Enquire / Visit Website / Save cluster. Same job — enquiry keeps the primary
 * slot, and the minority of products that can be bought or sampled keep their
 * ecom path one click away (the line opens the purchase drawer, whose footer
 * carries quantity + Add to Cart / Order Sample). Self-hides when there is
 * nothing to transact.
 */
export const ProductShopLine = ({
    isBuyable,
    isSampleBuyable,
    onOpen,
}: ProductShopBannerProps) => {
    if (!isBuyable && !isSampleBuyable) return null;

    const label = isBuyable
        ? 'Available to shop on ArchiPro'
        : 'Samples available to order on ArchiPro';

    return (
        <button
            type="button"
            onClick={onOpen}
            className="group inline-flex items-center gap-2 font-sans text-sm text-[#1f1f21]"
        >
            <ShoppingBag
                className="size-4 shrink-0 text-[#1f1f21]"
                strokeWidth={1.5}
            />
            <span className="leading-[1.2] font-medium tracking-[-0.01em]">
                {label}
            </span>
            <ChevronRight className="size-4 shrink-0 text-[#767676] transition-transform duration-200 ease-out group-hover:translate-x-0.5" />
        </button>
    );
};
